/**
 * AycanCoasterPicker — Web Component
 * Tek grid: müşteri tasarımları tıklama sırasıyla seçer, ardışık ikili
 * gruplar bir "çift" oluşturur. Qty = aynı tasarımın N ardışık tıklaması.
 * Sepet: her çift → { id: pair[0].variantId, quantity: 1, properties: { 'Pattern 2': pair[1].code } }
 */
class AycanCoasterPicker extends HTMLElement {
  connectedCallback() {
    this.variantMap = JSON.parse(this.querySelector('.aycan-variant-map').textContent);
    this.selection = []; // ordered by click sequence: { code, variantId, qty }
    this._lastClickedCode = null;

    // Modal yolunda ATC iki yerde: modal içinde + PDP'de (karar #4). İkisi birlikte yönetilir.
    this.atcBtns      = [...this.querySelectorAll('.aycan-coaster__atc')];
    this.atcBtn       = this.atcBtns[0] || null;
    this.errorEl      = this.querySelector('.aycan-coaster__error');
    this.clearBtn     = this.querySelector('.aycan-coaster__clear');
    this.atcLabel     = this.atcBtn?.querySelector('.aycan-atc__label');
    this._defaultAtcLabel = this.atcLabel?.textContent?.trim() || '';
    this._pairIncompleteLabel = this.dataset.pairIncompleteLabel || 'Add one more design to complete the pair';

    this._bindEvents();
    this._initModal();
    this._initSwatchImageFallback();
    this._validate();
  }

  /**
   * Modal picker — design picker ile aynı desen (bkz. aycan-design-picker.js).
   * Grid/qty/ATC hâlâ this kapsamında olduğu için diğer selector'lar değişmez.
   */
  /** Modalı açmanın TEK yolu — odak burada veriliyor (bkz. aycan-design-picker.js). */
  _openModal() {
    this.modal.showModal();
    this.modal.querySelector('.aycan-picker-modal__panel')?.focus({ preventScroll: true });
  }

  _initModal() {
    this.modal = this.querySelector('.aycan-picker-modal');
    this.trigger = this.querySelector('.aycan-picker-trigger');
    if (!this.modal || !this.trigger) return;

    this._desktopMq = window.matchMedia('(min-width: 750px)');

    this.trigger.addEventListener('click', () => {
      if (!this._desktopMq.matches) return;
      this._openModal();
      // showModal SONRASI: dialog kapalıyken gövdenin ölçüsü 0'dır
      this._fitModalGrid();
      this.trigger.setAttribute('aria-expanded', 'true');
      window.ewaTrack?.('pdp_picker_open', { picker_type: 'coaster' });
    });

    window.addEventListener('resize', () => {
      if (this.modal.open) this._fitModalGrid();
    });

    // Done ve × aynı işi yapar: seçimi koruyarak kapat (bkz. aycan-design-picker.js)
    this.modal.querySelectorAll('.aycan-picker-modal__close, .aycan-picker-modal__done').forEach((btn) => {
      btn.addEventListener('click', () => this.modal.close());
    });

    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) this.modal.close();
    });

    this.modal.addEventListener('close', () => {
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.focus({ preventScroll: true });
    });

    this._thumbsWrap = this.trigger.querySelector('.aycan-picker-trigger__thumbs');
    this._moreEl = this.trigger.querySelector('.aycan-picker-trigger__more');
    this._defaultThumbs = this._thumbsWrap
      ? [...this._thumbsWrap.children].map((node) => node.cloneNode(true))
      : [];
    this._defaultMoreText = this._moreEl?.textContent.trim() || '';
    this._previewMax = this._defaultThumbs.length || 8;
  }

  /**
   * Fill grid — sütun sayısı ürüne göre çözülür, sabit değil.
   * (Ayrıntılı gerekçe: aycan-design-picker.js `_fitModalGrid`.)
   */
  _fitModalGrid() {
    const body = this.modal?.querySelector('.aycan-picker-modal__body');
    const grid = body?.querySelector('.aycan-design-picker__grid');
    if (!body || !grid) return;

    const n = grid.querySelectorAll('.aycan-swatch-item').length;

    // clientWidth/Height padding'i içerir — düş (bkz. aycan-design-picker.js)
    const bcs = getComputedStyle(body);
    const w = body.clientWidth - parseFloat(bcs.paddingLeft) - parseFloat(bcs.paddingRight);
    const h = body.clientHeight - parseFloat(bcs.paddingTop) - parseFloat(bcs.paddingBottom);
    if (!n || w <= 0 || h <= 0) return;

    const cs = getComputedStyle(grid);
    const gap = parseFloat(cs.columnGap) || 12;
    const rowGap = parseFloat(cs.rowGap) || gap + 8;

    let cols = 0;
    let bestCell = 0;
    for (let c = 1; c <= n; c++) {
      const rows = Math.ceil(n / c);
      const cellW = (w - (c - 1) * gap) / c;
      const totalH = rows * cellW + (rows - 1) * rowGap;
      if (totalH <= h && cellW > bestCell) {
        bestCell = cellW;
        cols = c;
      }
    }

    if (cols) grid.style.setProperty('--modal-cols', cols);
  }

  _triggerThumb(code) {
    const span = document.createElement('span');
    span.className = 'aycan-picker-trigger__thumb';
    const item = this.querySelector(`.aycan-swatch-item[data-code="${CSS.escape(code)}"]`);
    const img = item?.querySelector('.aycan-swatch__btn--image img');
    if (img) {
      const clone = img.cloneNode();
      clone.removeAttribute('loading');
      clone.alt = '';
      span.appendChild(clone);
    }
    return span;
  }

  /**
   * Karar #3 + çift görünürlüğü: seçim özeti thumbnail'lar, ama coaster'da
   * sepete giden birim TEK ürün değil ÇİFT. Tetikleyici bu yüzden birimleri
   * tıklama sırasına göre ikişerli gruplar ve her çifti noktalı pill çerçeveye
   * alır — kullanıcı hangi desenin hangisiyle eşleştiğini modalı açmadan görür.
   * Tek kalan birim (tekil sayı) nötr çerçeveyle "tamamlanmamış" gösterilir;
   * ATC zaten o hâlde disabled ve "complete the pair" diyor.
   */
  _updateTriggerThumbs() {
    if (!this._thumbsWrap) return;

    const units = this._flattenUnits();

    if (!units.length) {
      this._thumbsWrap.replaceChildren(...this._defaultThumbs.map((n) => n.cloneNode(true)));
      if (this._moreEl) this._moreEl.textContent = this._defaultMoreText;
      return;
    }

    // Önizleme kapasitesi thumb cinsinden; bir çift 2 thumb tutar
    const maxPairs = Math.max(1, Math.floor(this._previewMax / 2));
    const groups = [];

    for (let i = 0; i < units.length && groups.length < maxPairs; i += 2) {
      const pair = document.createElement('span');
      pair.className = 'aycan-picker-trigger__pair';
      pair.appendChild(this._triggerThumb(units[i].code));
      if (units[i + 1]) {
        pair.appendChild(this._triggerThumb(units[i + 1].code));
      } else {
        pair.classList.add('is-incomplete');
      }
      groups.push(pair);
    }

    this._thumbsWrap.replaceChildren(...groups);
    if (this._moreEl) this._moreEl.textContent = this.dataset.changeLabel || this._defaultMoreText;
  }

  /** Seçim yokken ATC: masaüstünde modalı aç, mobilde grid'e kaydır. */
  _revealPicker() {
    if (this.modal && this._desktopMq?.matches) {
      this._openModal();
      this.trigger?.setAttribute('aria-expanded', 'true');
      return;
    }
    this._scrollToPicker();
  }

  _getAtcLabels() {
    const dock = document.getElementById(`pdp-mobile-dock-${this.dataset.sectionId}`);
    return {
      selectLabel: dock?.dataset.selectLabel || 'Select designs',
      defaultLabel: this._defaultAtcLabel || dock?.dataset.atcLabel || 'Add to cart',
    };
  }

  _scrollToPicker() {
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const first = this.querySelector('.aycan-swatch__btn:not([disabled])');
    first?.focus({ preventScroll: true });
  }

  /**
   * "Add 3 · $44.97" — karar #4. Coaster'da sayım birimi ÇİFT (pair), tekil ürün değil:
   * sepete giden her satır bir çift olduğu için kullanıcının gördüğü sayı da çift sayısı.
   */
  _composeAddLabel(fallback, pairCount) {
    const tpl = this.dataset.addLabel;
    if (!tpl) return fallback;
    const price = this._formatMoney(this._computeCoasterTotalCents());
    return tpl.replace('__COUNT__', String(pairCount)).replace('__PRICE__', price);
  }

  _setAtcState({ disabled, selectMode, text }) {
    this.atcBtns.forEach((btn) => {
      btn.disabled = disabled;
      btn.classList.toggle('is-select-mode', selectMode);
      const label = btn.querySelector('.aycan-atc__label');
      if (label) label.textContent = text;
    });
  }

  _applyBuyboxAtc(totalUnits, isValid, pairCount = 0) {
    if (!this.atcBtns.length) return;
    const { selectLabel, defaultLabel } = this._getAtcLabels();

    if (totalUnits === 0) {
      this._setAtcState({ disabled: false, selectMode: true, text: selectLabel });
      return;
    }

    this._setAtcState({
      disabled: !isValid,
      selectMode: false,
      text: isValid ? this._composeAddLabel(defaultLabel, pairCount) : this._pairIncompleteLabel,
    });
  }

  _bindEvents() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('.aycan-swatch__qty, .aycan-qty__minus, .aycan-qty__plus, .aycan-qty__input')) {
        return;
      }
      const btn = event.target.closest('.aycan-swatch__btn');
      if (!btn || btn.disabled || !this.contains(btn)) return;
      const item = btn.closest('.aycan-swatch-item');
      if (!item) return;
      this._toggleSwatch(item);
    });

    this.querySelectorAll('.aycan-qty__minus').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        this._adjustQty(btn.closest('.aycan-swatch-item'), -1);
      });
    });

    this.querySelectorAll('.aycan-qty__plus').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        this._adjustQty(btn.closest('.aycan-swatch-item'), 1);
      });
    });

    this.querySelectorAll('.aycan-qty__input').forEach((input) => {
      input.addEventListener('click', (event) => event.stopPropagation());
      input.addEventListener('change', () => {
        const item = input.closest('.aycan-swatch-item');
        if (!item) return;
        const val = Math.max(1, Math.min(99, parseInt(input.value, 10) || 1));
        input.value = val;
        this._syncItemQty(item, val);
      });
    });

    this.atcBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.selection.length === 0) {
          this._revealPicker();
          window.ewaTrack?.('pdp_atc_incomplete', { picker_type: 'coaster' });
          return;
        }
        this._addToCart();
      });
    });

    this.clearBtn?.addEventListener('click', () => {
      this._resetSelection();
    });
  }

  _getItemQty(item) {
    const input = item.querySelector('.aycan-qty__input');
    return input ? Math.max(1, Math.min(99, parseInt(input.value, 10) || 1)) : 1;
  }

  _syncItemQty(item, qty) {
    const code = item.dataset.code;
    const entry = this.selection.find((x) => x.code === code);
    if (entry) entry.qty = qty;
    this._updateCounts();
    this._validate();
  }

  _adjustQty(item, delta) {
    if (!item) return;
    const input = item.querySelector('.aycan-qty__input');
    if (!input) return;
    const val = Math.max(1, Math.min(99, (parseInt(input.value, 10) || 1) + delta));
    input.value = val;
    this._syncItemQty(item, val);
  }

  _setQtyVisible(item, visible) {
    const qtyEl = item.querySelector('.aycan-swatch__qty');
    if (!qtyEl) return;
    const qtyInputs = qtyEl.querySelectorAll('.aycan-qty__input, .aycan-qty__minus, .aycan-qty__plus');
    qtyEl.hidden = !visible;
    qtyEl.setAttribute('aria-hidden', visible ? 'false' : 'true');
    qtyInputs.forEach((el) => {
      el.tabIndex = visible ? 0 : -1;
    });
    if (!visible) {
      const input = qtyEl.querySelector('.aycan-qty__input');
      if (input) input.value = 1;
    }
  }

  _toggleSwatch(item) {
    const code = item.dataset.code;
    const idx  = this.selection.findIndex(x => x.code === code);
    const isNowSelected = idx === -1;

    item.classList.toggle('aycan-swatch-item--selected', isNowSelected);
    item.querySelector('.aycan-swatch__btn').setAttribute('aria-pressed', String(isNowSelected));

    if (isNowSelected) {
      const qty = this._getItemQty(item);
      this.selection.push({ code, variantId: parseInt(item.dataset.variantId, 10), qty });
      this._setQtyVisible(item, true);
      this._lastClickedCode = code;
    } else {
      this.selection.splice(idx, 1);
      this._setQtyVisible(item, false);
    }

    this._updateCounts();
    this._validate();
  }

  // Seçilen tasarımları qty kadar tekrarlayıp tıklama sırasına göre
  // düzleştirilmiş bir birim listesi üretir.
  _flattenUnits() {
    const units = [];
    for (const entry of this.selection) {
      for (let i = 0; i < (entry.qty || 1); i++) {
        units.push({ code: entry.code, variantId: entry.variantId });
      }
    }
    return units;
  }

  // Birim listesini ardışık ikişerli gruplar.
  _computePairs() {
    const units = this._flattenUnits();
    const pairs = [];
    for (let i = 0; i + 1 < units.length; i += 2) {
      pairs.push([units[i], units[i + 1]]);
    }
    return { pairs, totalUnits: units.length, isOdd: units.length % 2 === 1 };
  }

  _updateCounts() {
    const { pairs, totalUnits } = this._computePairs();
    const countEl = this.querySelector('.aycan-coaster__count');
    if (countEl) {
      const n = pairs.length;
      countEl.textContent = this._t('pairs_formed', n, null, `${n} pair${n === 1 ? '' : 's'} formed`);
      countEl.classList.toggle('is-empty', n === 0);
      countEl.setAttribute('aria-hidden', n === 0 ? 'true' : 'false');
    }
    if (this.clearBtn) this.clearBtn.hidden = totalUnits === 0;
  }

  _validate() {
    const { pairs, totalUnits, isOdd } = this._computePairs();
    const isValid = pairs.length > 0 && !isOdd;

    this._applyBuyboxAtc(totalUnits, isValid, pairs.length);
    this._updateTriggerThumbs();
    this._emitDockUpdate(isValid, pairs.length, totalUnits);
  }

  /**
   * Toplam = ÇİFT başına tek variant fiyatı. Variant fiyatı "Set of 2"nin fiyatıdır
   * ve sepete her çift için pair[0].variantId × 1 gider (bkz. _addToCart). Birimleri
   * toplamak fiyatı ikiye katlıyordu (1 çift → 2 × $22.99 gösteriyordu, sepet $22.99 alıyordu).
   * Eşleşmemiş tek birim (isOdd) fiyata girmez; ATC zaten o hâlde kilitli.
   */
  _computeCoasterTotalCents() {
    const { pairs } = this._computePairs();
    return pairs.reduce((sum, [first]) => {
      const el = this.querySelector(
        `.aycan-swatch-item[data-code="${CSS.escape(first.code)}"]`
      );
      const price = parseInt(el?.dataset.price, 10) || 0;
      return sum + price;
    }, 0);
  }

  _getDockThumb() {
    const el = this._lastClickedCode
      ? this.querySelector(`.aycan-swatch-item[data-code="${CSS.escape(this._lastClickedCode)}"]`)
      : null;
    const img = el?.querySelector('.aycan-swatch__btn--image img');
    if (img?.currentSrc || img?.src) return img.currentSrc || img.src;
    const dock = document.getElementById(`pdp-mobile-dock-${this.dataset.sectionId}`);
    const thumb = dock?.querySelector('.pdp-mobile-dock__thumb');
    return thumb?.tagName === 'IMG' ? thumb.src : '';
  }

  _emitDockUpdate(isValid, pairCount, totalUnits) {
    if (typeof window.pdpDockUpdate !== 'function') return;

    const computed = this._computePairs();
    const g1 = pairCount ?? computed.pairs.length;
    const units = totalUnits ?? computed.totalUnits;
    const hasItems = units > 0;
    const dock = document.getElementById(`pdp-mobile-dock-${this.dataset.sectionId}`);
    const selectLabel = dock?.dataset.selectLabel || 'Select designs';
    const defaultLabel = this._defaultAtcLabel || dock?.dataset.atcLabel || 'Add to cart';
    const valid = isValid ?? (g1 > 0 && units % 2 === 0);

    window.pdpDockUpdate(this.dataset.sectionId, {
      thumbSrc: this._getDockThumb(),
      // Fiyat yalnız oluşmuş çift varken; tek birimde "0 pairs · $0.00" yerine sade sayım
      selectionLine: hasItems
        ? `${this._t('pairs', g1, null, `${g1} pair${g1 === 1 ? '' : 's'}`)}${g1 > 0 ? ` · ${this._formatMoney(this._computeCoasterTotalCents())}` : ''}`
        : '',
      // The CTA keeps its own label; the "complete the pair" guidance goes to the dock's status pill
      // above the bar instead of overwriting the button text (which blew the button up on mobile).
      // The button still communicates the blocked state by staying disabled.
      ctaLabel: !hasItems ? selectLabel : defaultLabel,
      ctaDisabled: hasItems && !valid,
      ctaMode: !hasItems ? 'scroll' : 'add',
      notice: hasItems && !valid ? this._pairIncompleteLabel : '',
    });
  }

  /* Metin ve para: assets/aycan-picker-i18n.js, veri snippets/aycan-picker-strings.liquid.
     Yardımcı çağrı anında aranır (defer sırası garanti değil); yoksa İngilizce yedeğe düşer.
     Eskiden `Shopify.formatMoney` arıyordu ama o hiçbir yerde tanımlı değil: sabit `$`
     basılıyor, EUR'lu alıcı dolar işaretli tutar görüyordu. */
  _i18n() {
    const i18n = window.AycanPickerI18n;
    if (i18n && this._strings === undefined) this._strings = i18n.read(this);
    return i18n || null;
  }

  _t(key, qty, vars, fallback) {
    const i18n = this._i18n();
    const text = i18n ? i18n.plural(this._strings, key, qty, vars) : null;
    return text == null ? fallback : text;
  }

  _formatMoney(cents) {
    const i18n = this._i18n();
    return i18n ? i18n.money(this._strings, cents) : `$${(cents / 100).toFixed(2)}`;
  }

  async _addToCart() {
    const { pairs, isOdd } = this._computePairs();
    if (pairs.length === 0 || isOdd) return;

    const items = pairs.map(([a, b]) => ({
      id:         a.variantId,
      quantity:   1,
      properties: { 'Pattern 2': b.code },
    }));

    this._setLoading(true);

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      let sections = ['cart-icon-bubble'];
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        sections = cartDrawer.getSectionsToRender().map((s) => s.id);
      }

      const resp = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.description || 'Could not add to cart.');
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source:   'aycan-coaster-picker',
        cartData: data,
      });

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      }

      this._resetSelection();
      window.ewaTrack?.('pdp_add_to_cart', { picker_type: 'coaster', atc_items: items.length });
      // Ekleme başarılı → modal kapanır, drawer aynı yanıttan render edilir
      if (this.modal?.open) this.modal.close();
    } catch (err) {
      window.ewaTrack?.('pdp_atc_error', { picker_type: 'coaster', atc_error: err.message });
      if (this.errorEl) {
        this.errorEl.textContent = err.message;
        this.errorEl.hidden = false;
      }
    } finally {
      this._setLoading(false);
    }
  }

  _setLoading(isLoading) {
    this.atcBtns.forEach((btn) => {
      btn.disabled = isLoading;
      btn.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      btn.querySelector('.aycan-atc__label')?.classList.toggle('hidden', isLoading);
    });
    window.pdpDockUpdate?.(this.dataset.sectionId, { loading: isLoading });
    if (!isLoading) this._validate();
  }

  _resetSelection() {
    this.selection = [];
    this._lastClickedCode = null;
    this.querySelectorAll('.aycan-swatch-item').forEach(item => {
      item.classList.remove('aycan-swatch-item--selected');
      item.querySelector('.aycan-swatch__btn').setAttribute('aria-pressed', 'false');
      this._setQtyVisible(item, false);
    });
    this._updateCounts();
    this._validate();
    if (this.errorEl) this.errorEl.hidden = true;
  }

  _initSwatchImageFallback() {
    this.querySelectorAll('.aycan-swatch__btn--image img').forEach(img => {
      img.addEventListener('error', () => {
        const btn = img.closest('.aycan-swatch__btn');
        const figure = img.closest('.aycan-swatch__figure');
        btn.classList.remove('aycan-swatch__btn--image');
        figure?.remove();
        img.remove();
        btn.textContent = btn.dataset.fallback || '';
      }, { once: true });
    });
  }
}

customElements.define('aycan-coaster-picker', AycanCoasterPicker);
